/**
 * Alshallal Pastry Company HR System - Request Approvals Page
 * Phase 5: Generalized Requests & Approvals
 * Phase 25.1: Enhanced with Document Preview component
 * Manager/HR view for pending request approvals
 * 
 * RBAC:
 * - Manager: sees direct reports' pending_manager requests, can act only at pending_manager
 * - HR: sees all pending_hr requests, can act only at pending_hr
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  ClipboardList,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Building,
  Paperclip,
  Loader2,
  Eye,
  FileText,
  Download
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import DocumentPreview from '@/components/documents/DocumentPreview';
import { downloadWithAuth } from '@/lib/downloadWithAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RequestApprovalsPage = () => {
  const { token, hasRole } = useAuth();
  const { isRTL, language } = useLanguage();

  // State
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ open: false, request: null, action: null });
  const [actionReason, setActionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Annual deduction fields (HR only)
  const [deductFromAnnual, setDeductFromAnnual] = useState(false);
  const [annualDeductDays, setAnnualDeductDays] = useState('');
  const [annualDeductReason, setAnnualDeductReason] = useState('');

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, request: null });

  // Document preview dialog (Phase 25.1)
  const [previewDialog, setPreviewDialog] = useState({ open: false, attachment: null });

  const isHR = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const isManager = hasRole('manager');
  
  // Reset deduction fields when dialog closes
  const resetDeductionFields = () => {
    setDeductFromAnnual(false);
    setAnnualDeductDays('');
    setAnnualDeductReason('');
  };

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/requests/approvals/pending?page=${page}&page_size=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRequests(response.data.requests || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.detail?.error || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // Process approval action
  const handleAction = async () => {
    if (!actionDialog.request) return;
    if (actionDialog.action === 'reject' && actionReason.length < 10) return;
    
    // Validate deduction fields if enabled
    if (actionDialog.action === 'approve' && deductFromAnnual) {
      if (!annualDeductDays || parseFloat(annualDeductDays) <= 0) {
        setError(isRTL ? 'أدخل عدد الأيام للخصم' : 'Enter days to deduct');
        return;
      }
      if (!annualDeductReason || annualDeductReason.length < 5) {
        setError(isRTL ? 'سبب الخصم مطلوب (5 أحرف على الأقل)' : 'Deduction reason required (min 5 characters)');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const endpoint = isHR
        ? `${API_URL}/requests/${actionDialog.request.id}/hr-action`
        : `${API_URL}/requests/${actionDialog.request.id}/manager-action`;

      const payload = {
        action: actionDialog.action,
        reason: actionReason || undefined
      };
      
      // Add deduction fields for HR approval
      if (isHR && actionDialog.action === 'approve' && deductFromAnnual) {
        payload.deduct_from_annual = true;
        payload.annual_deduct_days = parseFloat(annualDeductDays);
        payload.annual_deduct_reason = annualDeductReason;
      }

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setActionDialog({ open: false, request: null, action: null });
      setActionReason('');
      resetDeductionFields();
      fetchPendingApprovals();
    } catch (err) {
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.detail || 'Action failed';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'PP');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="request-approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'موافقات الطلبات' : 'Request Approvals'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isHR
              ? (isRTL ? 'طلبات بانتظار موافقة الموارد البشرية' : 'Requests pending HR approval')
              : (isRTL ? 'طلبات فريقك بانتظار الموافقة' : 'Team requests pending your approval')}
          </p>
        </div>
        <Button variant="outline" onClick={fetchPendingApprovals} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ms-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700">{total}</p>
                <p className="text-sm text-amber-600">{isRTL ? 'بانتظار المراجعة' : 'Pending Review'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {isRTL ? 'لا توجد طلبات معلقة' : 'No Pending Requests'}
              </h3>
              <p className="text-slate-600">
                {isRTL ? 'جميع الطلبات تمت معالجتها' : 'All requests have been processed'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                    <TableHead>{isRTL ? 'القسم' : 'Department'}</TableHead>
                    <TableHead>{isRTL ? 'نوع الطلب' : 'Request Type'}</TableHead>
                    <TableHead>{isRTL ? 'تاريخ التقديم' : 'Submitted'}</TableHead>
                    <TableHead>{isRTL ? 'المرفقات' : 'Attachments'}</TableHead>
                    <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id} data-testid={`request-row-${request.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span className="font-medium">{request.employee_name?.trim() || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-slate-400" />
                          <span>{request.employee_department || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {language === 'ar' && request.request_type_name_ar ? request.request_type_name_ar : request.request_type_name}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(request.submitted_at)}
                      </TableCell>
                      <TableCell>
                        {request.attachments?.length > 0 && (
                          <Badge
                            variant="outline"
                            className="gap-1 border-blue-200 bg-blue-50 text-blue-700"
                            title={isRTL ? 'يوجد مستند مرفق' : 'A supporting document is attached'}
                            data-testid={`request-attachment-flag-${request.id}`}
                          >
                            <Paperclip className="h-3 w-3" />
                            {request.attachments.length}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setViewDialog({ open: true, request })}
                            data-testid={`view-btn-${request.id}`}
                          >
                            <Eye className="h-4 w-4 me-1" />
                            {isRTL ? 'عرض' : 'View'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => setActionDialog({ open: true, request, action: 'approve' })}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            <CheckCircle className="h-4 w-4 me-1" />
                            {isRTL ? 'موافقة' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setActionDialog({ open: true, request, action: 'reject' })}
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="h-4 w-4 me-1" />
                            {isRTL ? 'رفض' : 'Reject'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* View Request Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => !open && setViewDialog({ open: false, request: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {viewDialog.request && (language === 'ar' && viewDialog.request.request_type_name_ar ? viewDialog.request.request_type_name_ar : viewDialog.request?.request_type_name)}
            </DialogTitle>
            <DialogDescription>
              {viewDialog.request?.employee_name?.trim()} - {viewDialog.request?.employee_department}
            </DialogDescription>
          </DialogHeader>

          {viewDialog.request && (
            <div className="space-y-4 py-4">
              {/* Submitted */}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">{isRTL ? 'تاريخ التقديم:' : 'Submitted:'}</span>
                <span>{formatDate(viewDialog.request.submitted_at)}</span>
              </div>

              {/* Form Data */}
              {viewDialog.request.form_data && Object.keys(viewDialog.request.form_data).length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'التفاصيل:' : 'Details:'}</h4>
                  <div className="bg-slate-50 p-3 rounded space-y-1">
                    {Object.entries(viewDialog.request.form_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {viewDialog.request.notes && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'ملاحظات:' : 'Notes:'}</h4>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{viewDialog.request.notes}</p>
                </div>
              )}

              {/* Attachments */}
              {viewDialog.request.attachments?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">{isRTL ? 'المرفقات:' : 'Attachments:'}</h4>
                  {viewDialog.request.attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                      <FileText className="h-4 w-4 text-slate-500" />
                      <span className="flex-1 text-sm">{att.file_name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewDialog({ open: true, attachment: att })}
                        title={isRTL ? 'معاينة' : 'View on screen'}
                        data-testid={`request-attachment-preview-${att.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadWithAuth(
                          `${API_URL}/requests/attachments/${att.id}/download`, att.file_name, token,
                        )}
                        title={isRTL ? 'تحميل' : 'Download'}
                        data-testid={`request-attachment-download-${att.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setViewDialog({ open: false, request: null })}>
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                setViewDialog({ open: false, request: null });
                setActionDialog({ open: true, request: viewDialog.request, action: 'approve' });
              }}
            >
              <CheckCircle className="h-4 w-4 me-1" />
              {isRTL ? 'موافقة' : 'Approve'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setViewDialog({ open: false, request: null });
                setActionDialog({ open: true, request: viewDialog.request, action: 'reject' });
              }}
            >
              <XCircle className="h-4 w-4 me-1" />
              {isRTL ? 'رفض' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => {
        if (!open) {
          setActionDialog({ open: false, request: null, action: null });
          setActionReason('');
          resetDeductionFields();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={actionDialog.action === 'approve' ? 'text-green-600' : 'text-red-600'}>
              {actionDialog.action === 'approve'
                ? (isRTL ? 'الموافقة على الطلب' : 'Approve Request')
                : (isRTL ? 'رفض الطلب' : 'Reject Request')}
            </DialogTitle>
            {actionDialog.request && (
              <DialogDescription>
                {actionDialog.request.employee_name?.trim()} - {language === 'ar' && actionDialog.request.request_type_name_ar ? actionDialog.request.request_type_name_ar : actionDialog.request.request_type_name}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionDialog.action === 'reject' ? (
              <div className="space-y-2">
                <Label>{isRTL ? 'سبب الرفض (مطلوب)' : 'Rejection Reason (required)'}</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={isRTL ? 'أدخل سبب الرفض (10 أحرف على الأقل)...' : 'Enter rejection reason (min 10 characters)...'}
                  rows={3}
                  data-testid="action-reason-input"
                />
                {actionReason.length > 0 && actionReason.length < 10 && (
                  <p className="text-sm text-red-500">
                    {isRTL ? `${10 - actionReason.length} أحرف متبقية` : `${10 - actionReason.length} more characters needed`}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label>{isRTL ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={isRTL ? 'أضف ملاحظات...' : 'Add notes...'}
                  rows={2}
                />
              </div>
            )}
            
            {/* HR Deduction Controls - Only for HR on approval */}
            {isHR && actionDialog.action === 'approve' && (
              <div className="mt-4 p-4 border rounded-lg bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="deduct-annual"
                    checked={deductFromAnnual}
                    onChange={(e) => setDeductFromAnnual(e.target.checked)}
                    className="h-4 w-4 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                  />
                  <Label htmlFor="deduct-annual" className="text-amber-800 font-medium cursor-pointer">
                    {isRTL ? 'خصم من الإجازة السنوية' : 'Charge to Annual Leave'}
                  </Label>
                </div>
                
                {deductFromAnnual && (
                  <div className="space-y-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-amber-800">
                        {isRTL ? 'عدد الأيام للخصم' : 'Days to Deduct'}
                      </Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={annualDeductDays}
                        onChange={(e) => setAnnualDeductDays(e.target.value)}
                        placeholder={isRTL ? 'مثال: 2' : 'e.g., 2'}
                        className="border-amber-300 focus:border-amber-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-amber-800">
                        {isRTL ? 'سبب الخصم (مطلوب)' : 'Deduction Reason (required)'}
                      </Label>
                      <Textarea
                        value={annualDeductReason}
                        onChange={(e) => setAnnualDeductReason(e.target.value)}
                        placeholder={isRTL ? 'اذكر سبب خصم هذا الطلب من الإجازة السنوية...' : 'Explain why this request is charged to annual leave...'}
                        rows={2}
                        className="border-amber-300 focus:border-amber-500"
                      />
                    </div>
                    <p className="text-xs text-amber-700">
                      {isRTL 
                        ? '⚠️ سيتم خصم الأيام المحددة من رصيد الإجازة السنوية للموظف'
                        : '⚠️ The specified days will be deducted from employee\'s annual leave balance'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, request: null, action: null })}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={handleAction}
              disabled={submitting || (actionDialog.action === 'reject' && actionReason.length < 10)}
              className={actionDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-action-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" />{isRTL ? 'جاري المعالجة...' : 'Processing...'}</>
              ) : (
                actionDialog.action === 'approve'
                  ? (isRTL ? 'موافقة' : 'Approve')
                  : (isRTL ? 'رفض' : 'Reject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 25.1: Document Preview for Attachments */}
      <DocumentPreview
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, attachment: null })}
        document={previewDialog.attachment ? {
          id: previewDialog.attachment.id,
          file_name: previewDialog.attachment.file_name,
          mime_type: previewDialog.attachment.mime_type
        } : null}
        downloadUrl={previewDialog.attachment ? `${API_URL}/requests/attachments/${previewDialog.attachment.id}/download` : null}
        token={token}
        isRTL={isRTL}
        documentType="employee"
      />
    </div>
  );
};

export default RequestApprovalsPage;
